import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { generateUUID } from '@renderer/utils/idUtils'
import Preview from '@renderer/components/Preview'
import ComponentMapper from '@renderer/components/mapper/ComponentMapper'
import NewspaperLayout from './components/NewspaperLayout'
import { useTranslation } from 'react-i18next'
import { Package, RecordingType, WelcomePageSettings, Article } from '@renderer/types/configState'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { getNormalizedCaptureRatio as getSharedNormalizedCaptureRatio } from '@renderer/utils/captureRatioUtils'

interface WelcomePageProps {
  articles?: Article[]
}

const WelcomePage: React.FC<WelcomePageProps> = ({ articles }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { filters, config, getPageSettings } = useConfigStore()
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const { selectedCamera, selectedSecondaryCamera, cameraSettings } = useSettingsStore()
  const welcomeSettings = getPageSettings('welcome') as WelcomePageSettings | undefined
  const [isSessionStarting, setIsSessionStarting] = useState(false)
  const [city, setCity] = useState<string | null>(null)
  const [animationTime, setAnimationTime] = useState(0)
  /** Live preview box aspect (w/h); keeps fillContainer layout + L3 crop aligned with the template hole. */
  const [livePreviewSlotRatio, setLivePreviewSlotRatio] = useState(16 / 9)
  const livePreviewBoxRef = useRef<HTMLDivElement>(null)
  const currentYear = new Date().getFullYear()

  const getNormalizedCaptureRatio = (): number | undefined =>
    getSharedNormalizedCaptureRatio(cameraSettings.captureRatio)

  // Check if custom design should be used (from customTheme for newspaper themes)
  const useCustomDesign = config?.customTheme?.useCustomDesign ?? false
  const components = config?.pages?.welcome?.components
  const shouldUseComponentMapper = useCustomDesign && components && components.length > 0

  // Get articles from prop or config.customTheme (priority: prop > config)
  // Only show articles if they exist - don't show defaults if project has no articles configured
  const articlesToDisplay = articles || config?.customTheme?.articles || []

  // Get first filter (monochrome)
  const firstFilter = filters[0]
  const filterAdjustments = firstFilter?.adjustments

  // City detection using Geolocation API and reverse geocoding
  // Persist city detection across navigation using sessionStorage
  // Re-detect if user location has changed significantly
  useEffect(() => {
    const CITY_STORAGE_KEY = 'dreambooth-newspaper-city'
    const LOCATION_STORAGE_KEY = 'dreambooth-newspaper-location'

    // Calculate distance between two coordinates (Haversine formula, simplified)
    const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
      const R = 6371 // Earth's radius in km
      const dLat = ((lat2 - lat1) * Math.PI) / 180
      const dLon = ((lon2 - lon1) * Math.PI) / 180
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    }

    const detectCity = async (latitude: number, longitude: number): Promise<void> => {
      // Use OpenStreetMap Nominatim API for reverse geocoding
      try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
        console.log('[NEWSPAPER WELCOME] Reverse geocoding URL:', url)
        const response = await fetch(url, {
          headers: {
            'User-Agent': 'Dreambooth-Newspaper/1.0'
          }
        })

        if (response.ok) {
          const data = await response.json()
          console.log('[NEWSPAPER WELCOME] City detection data:', data)
          const address = data.address || {}
          // Prioritize county (kabupaten/kota) over village for Indonesian addresses
          const detectedCity =
            address.county ||
            address.city ||
            address.town ||
            address.municipality ||
            address.village ||
            null

          if (detectedCity) {
            // Store detected city and location in sessionStorage
            sessionStorage.setItem(CITY_STORAGE_KEY, detectedCity)
            sessionStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({ latitude, longitude }))
            setCity(detectedCity)
          }
        }
      } catch (fetchError) {
        console.log('[NEWSPAPER WELCOME] Reverse geocoding failed:', fetchError)
      }
    }

    const getCurrentLocation = async (): Promise<void> => {
      if (!navigator.geolocation) {
        console.log('[NEWSPAPER WELCOME] Geolocation not supported')
        return
      }

      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            (error) => {
              console.log('[NEWSPAPER WELCOME] Geolocation error:', error)
              // Handle specific error codes gracefully
              if (error.code === 1) {
                console.log('[NEWSPAPER WELCOME] User denied geolocation')
              } else if (error.code === 2) {
                console.log('[NEWSPAPER WELCOME] Position unavailable')
              } else if (error.code === 3) {
                console.log('[NEWSPAPER WELCOME] Geolocation timeout')
              }
              reject(error)
            },
            {
              timeout: 10000,
              enableHighAccuracy: false,
              maximumAge: 300000 // Accept cached position up to 5 minutes old
            }
          )
        })

        const { latitude, longitude } = position.coords

        // Check if we have stored location
        const storedLocationStr = sessionStorage.getItem(LOCATION_STORAGE_KEY)
        const storedCity = sessionStorage.getItem(CITY_STORAGE_KEY)

        if (storedLocationStr && storedCity) {
          try {
            const storedLocation = JSON.parse(storedLocationStr) as {
              latitude: number
              longitude: number
            }
            const distance = getDistance(
              storedLocation.latitude,
              storedLocation.longitude,
              latitude,
              longitude
            )

            // If user moved more than 5km, re-detect city
            if (distance > 5) {
              console.log(
                `[NEWSPAPER WELCOME] Location changed by ${distance.toFixed(2)}km, re-detecting city...`
              )
              await detectCity(latitude, longitude)
            } else {
              // Use stored city if location hasn't changed significantly
              setCity(storedCity)
            }
          } catch (parseError) {
            console.log('[NEWSPAPER WELCOME] Stored location is invalid:', parseError)
            // If stored location is invalid, re-detect
            await detectCity(latitude, longitude)
          }
        } else {
          // No stored location/city, detect for the first time
          await detectCity(latitude, longitude)
        }
      } catch (error) {
        console.log('[NEWSPAPER WELCOME] City detection failed:', error)
        // If geolocation fails but we have a stored city, use it as fallback
        const storedCity = sessionStorage.getItem(CITY_STORAGE_KEY)
        if (storedCity) {
          setCity(storedCity)
        }
      }
    }

    getCurrentLocation()
  }, [])

  // Match Preview strip / welcome hole: cover + crop to this box, not the camera box alone
  useEffect(() => {
    const el = livePreviewBoxRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect
      if (!cr || cr.width <= 0 || cr.height <= 0) return
      setLivePreviewSlotRatio(cr.width / cr.height)
    })
    ro.observe(el)
    return (): void => ro.disconnect()
  }, [])

  // Animation for button (bounce effect - up and down)
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationTime((prev) => prev + 16)
    }, 16)
    return (): void => clearInterval(interval)
  }, [])

  const handleStart = async (): Promise<void> => {
    if (isSessionStarting) {
      return // Prevent multiple calls
    }
    setIsSessionStarting(true)

    try {
      console.log('[NEWSPAPER WELCOME] Button clicked, starting session')
      if (!projectId) {
        console.error('[NEWSPAPER WELCOME] No projectId found')
        setIsSessionStarting(false)
        return
      }

      // Removed unused isBypass variable

      const sessionId = generateUUID()
      const mode = 'capture' as const

      // Get recording package to determine type
      const recordingType = config?.pages?.checkout?.settings?.packages?.find(
        (pkg: Package) =>
          pkg.type === 'recording' ||
          pkg.type === 'strip_live' ||
          pkg.type === 'strip_story' ||
          pkg.type === 'session_recording'
      )?.type as RecordingType

      // Set session ID FIRST before any logs are created
      setSessionState({
        id: sessionId,
        projectId,
        mode,
        timeline: {
          startTime: new Date()
        },
        userPreferences: {
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled,
          recordingType,
          filterId: firstFilter?._id // Set first filter as default
        },
        gallery: {
          id: `${9999999999999 - Date.now()}-${sessionId}`
        }
      })

      // Now log activity with the session ID set
      logActivity(`Session started in ${mode} mode.`)

      // Navigation logic matches view/Welcome.tsx exactly
      const frameEnabled = config?.pages.frame.settings.enabled || false
      const filterSettings = getPageSettings('filter')
      const applyAfterCapture = filterSettings?.applyAfterCapture || false

      let nextPage: string
      
      if (welcomeSettings?.startWithPayment) {
        // If startWithPayment is enabled, always go to checkout first
        nextPage = 'checkout'
      } else {
        if (applyAfterCapture) {
          if (frameEnabled) {
            nextPage = 'frame'
          } else {
            nextPage = 'capture'
          }
        } else {
          nextPage = 'filter'
        }
      }

      // Check for secondary camera
      if (
        selectedSecondaryCamera &&
        nextPage === 'filter' &&
        (await unifiedCameraUtils.isDeviceResponding('secondary'))
      ) {
        nextPage = 'camera'
      }

      // Delay session upload to allow more logs to be collected
      setTimeout(() => {
        uploadSessionState()
      }, 2000)

      console.log('[NEWSPAPER WELCOME] Navigating to:', `/${projectId}/${nextPage}`)
      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('[NEWSPAPER WELCOME] Error starting session:', error)
    } finally {
      setIsSessionStarting(false)
    }
  }

  // If custom design is enabled and components exist, use ComponentMapper
  if (shouldUseComponentMapper) {
    return (
      <div className="relative w-full h-full">
        {components && <ComponentMapper components={components} />}
      </div>
    )
  }

  // Otherwise, use the default JSON-based newspaper layout
  return (
    <NewspaperLayout disableOverflow={true}>
      <div className="flex flex-col h-full relative overflow-hidden gap-2">
        {/* Live Camera Preview Section */}
        <div className="w-full flex-shrink-0 border-2 border-double border-[#2a2a2a] bg-newspaper-white p-2 flex flex-col h-[450px]">
          <div className="text-center mb-1 flex-shrink-0">
            <div className="font-newspaper italic text-xl leading-snug text-[#1a1a1a]">
              {((): React.ReactNode => {
                // Utility to trim 'Regency' if present
                const trimRegency = (city: string | null): string | null => {
                  if (!city) return null
                  return city.includes('Regency')
                    ? city.replace(/ ?Regency/i, '').trim()
                    : city
                }
                const displayCity = trimRegency(city)
                return displayCity
                  ? t('newspaper.welcome.subheadline', "{{city}}'s Most Beautiful {{year}}", {
                      city: displayCity,
                      year: currentYear
                    })
                  : t('newspaper.welcome.subheadlineDefault', "World's Most Beautiful {{year}}", {
                      year: currentYear
                    })
              })()}
            </div>
          </div>
          <div ref={livePreviewBoxRef} className="relative w-full flex-1 min-h-0 overflow-hidden">
            <Preview
              key={`preview-${selectedCamera || 'default'}`}
              adjustments={filterAdjustments}
              ratio={livePreviewSlotRatio}
              cameraRatio={getNormalizedCaptureRatio()}
              cameraKey="primary"
              filter={firstFilter?._id}
              fillContainer={true}
            />
          </div>
        </div>

        {/* Headline Section */}
        <div className="w-full flex-shrink-0 mb-2">
          <div className="border-t-2 border-[#2a2a2a] my-4"></div>
          {((): React.ReactNode => {
            // Function to trim "Regency" from the city name if present
            const getCityForHeadline = (city: string | null): string | null => {
              if (!city) return null
              return city.includes('Regency')
                ? city.replace(/ ?Regency/i, '').trim()
                : city
            }
            const cityTrimmed = getCityForHeadline(city)
            return (
              <div className="font-serif font-bold text-7xl leading-tight uppercase tracking-wider text-[#1a1a1a] text-left py-2">
                {config?.customTheme?.newspaperHeadline
                  ? config.customTheme.newspaperHeadline
                      .replace('{city}', cityTrimmed ? `${cityTrimmed.toUpperCase()} ` : 'LOCAL CITIZENS')
                  : t('newspaper.welcome.headline', 'Newspaper Photobooth: {{content}}', {
                      content: cityTrimmed
                        ? `${cityTrimmed.toUpperCase()} CITIZENS GATHER FOR HISTORIC PHOTO OPPORTUNITY`
                        : 'LOCAL CITIZENS GATHER FOR HISTORIC PHOTO OPPORTUNITY'
                    })}
              </div>
            )
          })()}
          <div className="border-t-2 border-[#2a2a2a] my-4"></div>
        </div>

        {/* Article Text */}
        <div className="w-full flex-1 relative overflow-hidden min-h-0">
          <div className="font-newspaper text-base leading-relaxed text-justify text-[#1a1a1a] text-xl h-full overflow-y-auto break-words pb-0 columns-2 gap-4">


            {/* Render articles from config */}
            {articlesToDisplay.map((article, articleIndex) => (
              <React.Fragment key={articleIndex}>
                <h4 className="font-bold text-2xl mb-3 mt-5">
                  {article.heading}
                </h4>
                {article.paragraphs.map((paragraph, paragraphIndex) => (
                  <p
                    key={paragraphIndex}
                    className={
                      articleIndex === articlesToDisplay.length - 1 &&
                      paragraphIndex === article.paragraphs.length - 1
                        ? 'text-xl'
                        : 'mb-3 text-xl'
                    }
                  >
                    {paragraph}
                  </p>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Next Page Button - Large Bottom Center */}
        <div className="absolute bottom-6 left-1/2 z-10 flex justify-center w-full -translate-x-1/2">
          {((): React.ReactNode => {
            // Bounce animation - up and down movement
            const duration = 1000 // 1 second per cycle
            const progress = (animationTime % duration) / duration
            const bounceY = Math.sin(progress * Math.PI * 2) * 50 // 50px vertical movement
            const transformStyle = `translateY(${bounceY}px)`

            return (
              <button
                type="button"
                onClick={handleStart}
                disabled={isSessionStarting}
                style={{ transform: transformStyle }}
                className="hover:opacity-90 cursor-pointer font-serif font-bold transition-all duration-500 select-none active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-[#1a1a1a] text-[#fafaf8] border-2 border-double border-[#2a2a2a] text-4xl px-16 py-5 min-w-[320px] tracking-wider rounded-none shadow-none"
              >
                {isSessionStarting ? t('common.loading', 'Starting...') : t('newspaper.welcome.takePhoto', 'Take Photo')}
              </button>
            )
          })()}
        </div>
      </div>
    </NewspaperLayout>
  )
}

export default WelcomePage
